import { 
  PermissionAction, 
  PermissionResource, 
  ProfileData 
} from "@/entities/user";

/**
 * Check if a user has a specific permission
 * @param profileData - User profile data from API
 * @param resource - Resource to check
 * @param action - Action to check
 * @returns boolean
 */
export const hasPermission = (
  profileData: ProfileData | null,
  resource: PermissionResource,
  action: PermissionAction
): boolean => {
  if (!profileData) return false;

  // Super admin has all permissions
  if (profileData.user.isSuperAdmin) {
    return true;
  }

  // Check store permissions
  const permissions = profileData.store?.permissions?.[resource];
  if (!permissions) return false;

  return permissions.includes(action);
};

/**
 * Check if user has any of the specified permissions
 * @param profileData - User profile data from API
 * @param checks - Array of permission checks
 * @returns boolean
 */
export const hasAnyPermission = (
  profileData: ProfileData | null,
  checks: Array<{ resource: PermissionResource; action: PermissionAction }>
): boolean => {
  return checks.some(({ resource, action }) => 
    hasPermission(profileData, resource, action)
  );
};

/**
 * Check if user has all of the specified permissions
 * @param profileData - User profile data from API
 * @param checks - Array of permission checks
 * @returns boolean
 */
export const hasAllPermissions = (
  profileData: ProfileData | null,
  checks: Array<{ resource: PermissionResource; action: PermissionAction }>
): boolean => {
  return checks.every(({ resource, action }) => 
    hasPermission(profileData, resource, action)
  );
};

/**
 * Check if user has a specific role
 * @param profileData - User profile data from API
 * @param role - Role to check
 * @returns boolean
 */
export const hasRole = (
  profileData: ProfileData | null,
  role: string
): boolean => {
  if (!profileData) return false;
  if (profileData.user.isSuperAdmin) return true;
  return profileData.store?.role === role;
};

/**
 * Check if user has any of the specified roles
 * @param profileData - User profile data from API
 * @param roles - Array of roles to check
 * @returns boolean
 */
export const hasAnyRole = (
  profileData: ProfileData | null,
  roles: string[]
): boolean => {
  if (!profileData) return false;
  if (profileData.user.isSuperAdmin) return true;
  return roles.includes(profileData.store?.role || '');
};

/**
 * Get all permissions for a specific resource
 * @param profileData - User profile data from API
 * @param resource - Resource to get permissions for
 * @returns Array of actions user can perform on the resource
 */
export const getResourcePermissions = (
  profileData: ProfileData | null,
  resource: PermissionResource
): PermissionAction[] => {
  if (!profileData) return [];
  
  if (profileData.user.isSuperAdmin) {
    return ['read', 'create', 'update', 'delete'];
  }

  return profileData.store?.permissions?.[resource] || [];
};  